const DB_NAME = 'emberDraftState';
const STORE_NAME = 'state';

function isIndexedDBAvailable() {
  return typeof indexedDB !== 'undefined' && indexedDB !== null;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!isIndexedDBAvailable()) {
      reject(new Error('IndexedDB is not available'));
      return;
    }

    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error ?? new Error('Failed to open IndexedDB'));
    };
  });
}

export async function saveDraftState<T>(key: string, data: T): Promise<void> {
  try {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put(data, key);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        const err = tx.error ?? new Error('Failed to save draft state');
        db.close();
        reject(err);
      };
      tx.onabort = () => {
        const err = tx.error ?? new Error('Draft state transaction aborted');
        db.close();
        reject(err);
      };
    });
  } catch (error) {
    console.warn('saveDraftState failed', error);
  }
}

export async function loadDraftState<T>(key: string): Promise<T | null> {
  try {
    const db = await openDatabase();
    return await new Promise<T | null>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => {
        resolve((request.result as T | undefined) ?? null);
      };

      request.onerror = () => {
        reject(request.error ?? new Error('Failed to load draft state'));
      };

      tx.oncomplete = () => {
        db.close();
      };

      tx.onerror = () => {
        const err = tx.error ?? new Error('Failed to read draft state');
        db.close();
        reject(err);
      };
    });
  } catch (error) {
    console.warn('loadDraftState failed', error);
    return null;
  }
}

export async function clearDraftState(key: string): Promise<void> {
  try {
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.delete(key);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        const err = tx.error ?? new Error('Failed to clear draft state');
        db.close();
        reject(err);
      };
      tx.onabort = () => {
        const err = tx.error ?? new Error('Draft state transaction aborted');
        db.close();
        reject(err);
      };
    });
  } catch (error) {
    console.warn('clearDraftState failed', error);
  }
}


